/**
 * Vector appearance and effects (Shape properties inspector): appearance
 * stacks, live effects, variable-width strokes, recolour, blends, offset
 * paths, Live Paint, Transform Each and Graphic Styles. Swatch-linked paint
 * (`swatches.apply`) and colour reduction (`vector.reduceColours`) are the
 * Global Swatches panel's commands in `resources.ts`.
 *
 * Every command compiles to the native command the inspector executed
 * (`UpdateVectorObjectCommand`, `UpdateVectorSceneCommand`,
 * `ApplyGraphicStyleCommand`, `SetGraphicStylesCommand`), with the update
 * closure built from serializable params inside `compile`.
 *
 * Bundle: this module is reached statically from `user-dispatch.ts` (through
 * `command.run` and the registry), so it holds only definitions, validation
 * and data. Everything that needs vector code (`vector/runtime.ts` and the
 * effect modules) lives in `vector-appearance-compile.ts`, which is loaded:
 * - by `prepare` (a dynamic import) on the agent path, and
 * - statically by `VectorInspector.tsx`, itself a lazy chunk, which installs
 *   the compilers before the UI can run one of these commands synchronously.
 */
import type { LayerId, Ref } from '@maipo/agent-protocol';
import type { EditorDocument, VectorLayer } from '../../core/model';
import type { VectorObject } from '../../vector/types';
import { IntentRejection, type CompiledStep, type Diagnostic, type Footprint, type ValidateContext } from '../registry';
import { checkUnlocked, error, layerFor, staticIds, type LockMode } from '../intents/common';
import { defineCommand, DOCUMENT_RESOURCES, type EditorCommand } from './registry';

// ---------------------------------------------------------------------------
// Params (mirror maipo-commands-0.1-draft.schema.json)
// ---------------------------------------------------------------------------

type Hex = string;
interface ObjectTarget { layer: Ref; object: string }
interface ObjectsTarget { layer: Ref; objects: string[] }

export type AppearanceEntryInput =
  | { type: 'fill'; color?: Hex; alpha?: number }
  | { type: 'stroke'; color?: Hex; alpha?: number; width?: number }
  | { type: 'pattern'; motif?: 'dots' | 'stripes' | 'grid'; foreground?: Hex; background?: Hex; alpha?: number; tileWidth?: number; tileHeight?: number; offsetX?: number; offsetY?: number };

export interface AppearanceEntryPatch {
  color?: Hex; alpha?: number; width?: number;
  motif?: 'dots' | 'stripes' | 'grid'; foreground?: Hex; background?: Hex; tileWidth?: number; tileHeight?: number; offsetX?: number; offsetY?: number;
}

export interface TypeOnPathSettings { text?: string; family?: string; size?: number; color?: Hex; offset?: number }
export interface VectorBrushSettings { preset?: 'calligraphic' | 'art' | 'scatter'; color?: Hex; size?: number; spacing?: number }
export interface HalftoneSettings { mode?: 'mono' | 'cmyk'; cellSize?: number; angle?: number; density?: number; paperColor?: Hex }
export interface TransformEffectSettings { copies?: number; moveX?: number; moveY?: number; rotate?: number; scaleX?: number; scaleY?: number }
export interface EnvelopeSettings { preset?: 'arc' | 'flag' | 'bulge'; strength?: number }
export interface GradientMeshSettings { rows?: number; columns?: number; topLeft?: Hex; topRight?: Hex; bottomRight?: Hex; bottomLeft?: Hex }
export interface TransformEachInput { scaleX?: number; scaleY?: number; rotate?: number; moveX?: number; moveY?: number; randomize?: boolean; seed?: number; scaleVariation?: number; rotateVariation?: number; moveVariation?: number }

export interface VectorAppearanceParams {
  'vector.appearance.add': ObjectTarget & { entry: AppearanceEntryInput };
  'vector.appearance.update': ObjectTarget & { entry: string; set: AppearanceEntryPatch };
  'vector.appearance.remove': ObjectTarget & { entry: string };
  'vector.effects.typeOnPath': ObjectTarget & { settings: TypeOnPathSettings | null };
  'vector.effects.brush': ObjectTarget & { settings: VectorBrushSettings | null };
  'vector.effects.halftone': ObjectTarget & { settings: HalftoneSettings | null };
  'vector.effects.transform': ObjectTarget & { settings: TransformEffectSettings | null };
  'vector.effects.envelope': ObjectTarget & { settings: EnvelopeSettings | null };
  'vector.effects.gradientMesh': ObjectTarget & { settings: GradientMeshSettings | null };
  'vector.effects.expand': ObjectTarget & { effect: 'halftone' | 'transform' | 'envelope' };
  'vector.symbols.spray': ObjectTarget & { count?: number };
  'vector.symbols.clear': ObjectTarget;
  'vector.widthProfile.addStop': ObjectTarget & { t?: number; width?: number };
  'vector.widthProfile.updateStop': ObjectTarget & { stop: string; t?: number; width?: number };
  'vector.widthProfile.removeStop': ObjectTarget & { stop: string };
  'vector.widthProfile.reset': ObjectTarget;
  'vector.strokeStyle': ObjectTarget & { lineCap?: 'butt' | 'round' | 'square'; lineJoin?: 'miter' | 'round' | 'bevel'; dash?: number[] };
  'vector.opacity': ObjectTarget & { opacity: number };
  'vector.transformEach': ObjectsTarget & TransformEachInput;
  'vector.recolor': ObjectsTarget & { degrees: number };
  'vector.blend': { layer: Ref; from: string; to: string; steps?: number };
  'vector.offsetPath': ObjectsTarget & { distance: number };
  'vector.livePaint.make': ObjectsTarget;
  'vector.livePaint.fill': ObjectsTarget & { color: Hex };
  'vector.livePaint.expand': ObjectTarget;
  'graphicStyles.apply': ObjectsTarget & { style: string; mode?: 'replace' | 'merge' };
  'graphicStyles.save': ObjectTarget & { name: string };
  'graphicStyles.redefine': ObjectTarget & { style: string };
  'graphicStyles.rename': { style: string; name: string };
  'graphicStyles.duplicate': { style: string };
  'graphicStyles.delete': { style: string };
}

export type VectorAppearanceCommandId = keyof VectorAppearanceParams;

// ---------------------------------------------------------------------------
// The inspector's defaults (what "Add", "Apply" and "Create" insert)
// ---------------------------------------------------------------------------

/** Key order matters: it is the byte order of the saved project. */
export const APPEARANCE_DEFAULTS = Object.freeze({
  fill: { type: 'fill', color: '#f2b84b', alpha: 0.75 },
  stroke: { type: 'stroke', color: '#ffffff', alpha: 1, width: 6 },
  pattern: { type: 'pattern', motif: 'dots', foreground: '#163c2a', background: '#f6efe1', alpha: 1, tileWidth: 24, tileHeight: 24, offsetX: 0, offsetY: 0 },
} as const);

export const EFFECT_DEFAULTS = Object.freeze({
  typeOnPath: { text: 'Type on a path', family: 'Inter, sans-serif', size: 24, color: '#163c2a', offset: 0 },
  brush: { preset: 'calligraphic', color: '#163c2a', size: 12, spacing: 20 },
  halftone: { mode: 'mono', cellSize: 14, angle: 15, density: 0.85, paperColor: '#fffaf0' },
  transform: { copies: 4, moveX: 16, moveY: 16, rotate: 0, scaleX: 100, scaleY: 100 },
  envelope: { preset: 'arc', strength: 30 },
  gradientMesh: { rows: 2, columns: 2, topLeft: '#ff5f6d', topRight: '#ffc371', bottomRight: '#47c9e5', bottomLeft: '#6a67ce' },
} as const);

export const TRANSFORM_EACH_DEFAULTS = Object.freeze({ scaleX: 100, scaleY: 100, rotate: 0, moveX: 0, moveY: 0, randomize: false, seed: 1, scaleVariation: 0, rotateVariation: 0, moveVariation: 0 });

export const SYMBOL_SPRAY_DEFAULT = 25;
export const BLEND_STEPS_DEFAULT = 3;
export const MAX_GRAPHIC_STYLES = 128;

/** Extension keys read by validation (kept here so this module stays free of vector code). */
const EXT = Object.freeze({
  appearance: 'maipo:appearance',
  halftone: 'maipo:vector-halftone',
  transform: 'maipo:transform-effect',
  envelope: 'maipo:envelope-distort',
  livePaintFace: 'maipo:live-paint-face',
  graphicStyles: 'maipo:graphic-styles',
});

// ---------------------------------------------------------------------------
// Compilers (installed by vector-appearance-compile.ts)
// ---------------------------------------------------------------------------

export type VectorAppearanceCompilers = {
  [K in VectorAppearanceCommandId]: (document: EditorDocument, params: VectorAppearanceParams[K], aliases: ReadonlyMap<string, LayerId>) => CompiledStep[];
};

let installed: VectorAppearanceCompilers | null = null;

/** Called once by vector-appearance-compile.ts when the vector chunk loads. */
export function installVectorAppearanceCompilers(compilers: VectorAppearanceCompilers): void {
  installed = compilers;
}

async function loadCompilers(): Promise<VectorAppearanceCompilers> {
  const module = await import('./vector-appearance-compile');
  return module.VECTOR_APPEARANCE_COMPILERS;
}

function compilersFor(prepared: unknown): VectorAppearanceCompilers {
  const compilers = (prepared as VectorAppearanceCompilers | undefined) ?? installed;
  if (!compilers) throw new IntentRejection('COMMAND_REJECTED', 'The vector subsystem is not loaded yet; run this command through the dispatcher, which loads it.');
  return compilers;
}

// ---------------------------------------------------------------------------
// Validation helpers (raw document reads only)
// ---------------------------------------------------------------------------

function vectorLayerFor(document: EditorDocument, ref: Ref, context: ValidateContext, diagnostics: Diagnostic[], mode: LockMode = 'any'): VectorLayer | null {
  const layer = layerFor(document, ref, context, diagnostics);
  if (!layer) return null;
  if (layer.kind !== 'vector') {
    diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is a ${layer.kind} layer, not vector.`, layer.id));
    return null;
  }
  checkUnlocked(document, layer, mode, diagnostics, context.policy);
  return layer;
}

function objectOf(layer: VectorLayer, id: string, diagnostics: Diagnostic[]): VectorObject | null {
  const object = layer.vector.objects.find((candidate) => candidate.id === id);
  if (!object) diagnostics.push(error('LAYER_NOT_FOUND', `Vector object ${JSON.stringify(id).slice(0, 80)} does not exist on ${layer.id}.`, layer.id));
  return object ?? null;
}

function objectsOf(layer: VectorLayer, ids: readonly string[], diagnostics: Diagnostic[]): VectorObject[] {
  return ids.flatMap((id) => { const object = objectOf(layer, id, diagnostics); return object ? [object] : []; });
}

function requirePath(layer: VectorLayer, object: VectorObject, what: string, diagnostics: Diagnostic[]): void {
  if (object.type !== 'path') diagnostics.push(error('UNSUPPORTED_FOR_KIND', `${what} applies to paths; object ${object.id} is a ${object.type}.`, layer.id));
}

function targetObject(document: EditorDocument, params: ObjectTarget, context: ValidateContext, diagnostics: Diagnostic[]): { layer: VectorLayer; object: VectorObject } | null {
  const layer = vectorLayerFor(document, params.layer, context, diagnostics);
  if (!layer) return null;
  const object = objectOf(layer, params.object, diagnostics);
  return object ? { layer, object } : null;
}

function appearanceIds(object: VectorObject): { id: string; type: unknown }[] {
  const raw = object.extensions[EXT.appearance];
  return Array.isArray(raw) ? raw.filter((entry): entry is { id: string; type: unknown } => Boolean(entry) && typeof (entry as { id?: unknown }).id === 'string') : [];
}

function hasExtension(object: VectorObject, key: string): boolean {
  const value = object.extensions[key];
  return Boolean(value) && typeof value === 'object';
}

function rawStyleIds(document: EditorDocument): string[] {
  const raw = document.extensions[EXT.graphicStyles];
  return Array.isArray(raw) ? raw.flatMap((style) => (style && typeof style === 'object' && typeof (style as { id?: unknown }).id === 'string' ? [(style as { id: string }).id] : [])) : [];
}

function requireStyle(document: EditorDocument, id: string, diagnostics: Diagnostic[]): void {
  if (!rawStyleIds(document).includes(id)) diagnostics.push(error('COMMAND_REJECTED', `Graphic Style ${JSON.stringify(id).slice(0, 80)} does not exist.`));
}

// ---------------------------------------------------------------------------
// Footprints
// ---------------------------------------------------------------------------

function layerWrite(ref: Ref, aliases: ReadonlyMap<string, LayerId>, ...reads: string[]): Footprint {
  const ids = staticIds([ref], aliases);
  return { reads: [...ids, ...reads], writes: ids, structural: false, document: false };
}

function stylesWrite(ref: Ref | undefined, aliases: ReadonlyMap<string, LayerId>): Footprint {
  const ids = ref === undefined ? [] : staticIds([ref], aliases);
  return { reads: [...ids, DOCUMENT_RESOURCES.styles], writes: [DOCUMENT_RESOURCES.styles], structural: false, document: false };
}

// ---------------------------------------------------------------------------
// Definition helpers
// ---------------------------------------------------------------------------

const OBJECT_LABEL = 'Edit vector object';

interface Spec<K extends VectorAppearanceCommandId> {
  id: K;
  group?: EditorCommand['group'];
  backs: readonly string[];
  label: string | ((params: VectorAppearanceParams[K], document: EditorDocument) => string);
  footprint?: (document: EditorDocument, params: VectorAppearanceParams[K], aliases: ReadonlyMap<string, LayerId>) => Footprint;
  validate: (document: EditorDocument, params: VectorAppearanceParams[K], context: ValidateContext) => Diagnostic[];
}

function vectorCommand<K extends VectorAppearanceCommandId>(spec: Spec<K>): EditorCommand {
  const label = spec.label;
  return defineCommand<VectorAppearanceParams[K]>({
    id: spec.id,
    group: spec.group ?? 'vector',
    backs: spec.backs,
    label: typeof label === 'string' ? () => label : label,
    footprint: spec.footprint ?? ((_document, params, aliases) => layerWrite((params as { layer: Ref }).layer, aliases)),
    validate: spec.validate,
    prepare: () => loadCompilers(),
    compile: (document, params, prepared, aliases) => (compilersFor(prepared)[spec.id] as (d: EditorDocument, p: VectorAppearanceParams[K], a: ReadonlyMap<string, LayerId>) => CompiledStep[])(document, params, aliases),
  });
}

/** One-object edits: layer must be an unlocked vector layer holding the object. */
function objectCommand<K extends VectorAppearanceCommandId>(id: K, backs: readonly string[], check?: (layer: VectorLayer, object: VectorObject, params: VectorAppearanceParams[K], diagnostics: Diagnostic[]) => void, label: Spec<K>['label'] = OBJECT_LABEL): EditorCommand {
  return vectorCommand<K>({
    id,
    backs,
    label,
    validate: (document, params, context) => {
      const diagnostics: Diagnostic[] = [];
      const target = targetObject(document, params as unknown as ObjectTarget, context, diagnostics);
      if (target) check?.(target.layer, target.object, params, diagnostics);
      return diagnostics;
    },
  });
}

const backs = (...ids: string[]) => ids.map((id) => `panel.vector-inspector.${id}`);

// ---------------------------------------------------------------------------
// Appearance stack
// ---------------------------------------------------------------------------

const PATCH_KEYS: Record<string, readonly (keyof AppearanceEntryPatch)[]> = {
  fill: ['color', 'alpha'],
  stroke: ['color', 'alpha', 'width'],
  pattern: ['motif', 'foreground', 'background', 'alpha', 'tileWidth', 'tileHeight', 'offsetX', 'offsetY'],
};

export const appearanceAdd = objectCommand('vector.appearance.add', backs('appearance-add-fill', 'appearance-add-stroke', 'appearance-add-pattern'));

export const appearanceUpdate = objectCommand('vector.appearance.update', backs(
  'appearance-fill-color', 'appearance-fill-opacity', 'appearance-stroke-color', 'appearance-stroke-width',
  'appearance-pattern-motif', 'appearance-pattern-ink', 'appearance-pattern-paper', 'appearance-pattern-tile-width',
  'appearance-pattern-tile-height', 'appearance-pattern-offset-x', 'appearance-pattern-offset-y',
), (layer, object, params, diagnostics) => {
  const entry = appearanceIds(object).find((candidate) => candidate.id === params.entry);
  if (!entry) { diagnostics.push(error('COMMAND_REJECTED', `Appearance entry ${JSON.stringify(params.entry).slice(0, 80)} does not exist on object ${object.id}.`, layer.id)); return; }
  const allowed = PATCH_KEYS[String(entry.type)] ?? [];
  const foreign = Object.keys(params.set).filter((key) => !allowed.includes(key as keyof AppearanceEntryPatch));
  if (foreign.length) diagnostics.push(error('COMMAND_REJECTED', `A ${String(entry.type)} appearance entry has no ${foreign.join(', ')}; it accepts ${allowed.join(', ')}.`, layer.id));
});

export const appearanceRemove = objectCommand('vector.appearance.remove', backs('appearance-remove'), (layer, object, params, diagnostics) => {
  if (!appearanceIds(object).some((candidate) => candidate.id === params.entry)) diagnostics.push(error('COMMAND_REJECTED', `Appearance entry ${JSON.stringify(params.entry).slice(0, 80)} does not exist on object ${object.id}.`, layer.id));
});

// ---------------------------------------------------------------------------
// Live effects
// ---------------------------------------------------------------------------

export const typeOnPath = objectCommand('vector.effects.typeOnPath', backs('type-on-path-add', 'type-on-path-text', 'type-on-path-size', 'type-on-path-offset', 'type-on-path-color', 'type-on-path-remove'),
  (layer, object, _params, diagnostics) => requirePath(layer, object, 'Type on a Path', diagnostics));

export const vectorBrush = objectCommand('vector.effects.brush', backs('vector-brush-apply', 'vector-brush-preset', 'vector-brush-size', 'vector-brush-spacing', 'vector-brush-color', 'vector-brush-remove'),
  (layer, object, _params, diagnostics) => requirePath(layer, object, 'A vector brush', diagnostics));

export const halftone = objectCommand('vector.effects.halftone', backs('halftone-apply', 'halftone-mode', 'halftone-cell-size', 'halftone-angle', 'halftone-density', 'halftone-paper', 'halftone-remove'),
  (layer, object, params, diagnostics) => {
    if (params.settings && !hasExtension(object, EXT.halftone) && !object.fill) diagnostics.push(error('COMMAND_REJECTED', `Object ${object.id} has no fill; a halftone screens the fill colour. Give it a fill first.`, layer.id));
  });

export const transformEffect = objectCommand('vector.effects.transform', backs('transform-effect-apply', 'transform-effect-params', 'transform-effect-remove'));

export const envelope = objectCommand('vector.effects.envelope', backs('envelope-apply', 'envelope-preset', 'envelope-strength', 'envelope-remove'));

export const gradientMesh = objectCommand('vector.effects.gradientMesh', backs('gradient-mesh-create', 'gradient-mesh-rows', 'gradient-mesh-columns', 'gradient-mesh-corner-color', 'gradient-mesh-remove'));

const EXPAND_KEYS = { halftone: EXT.halftone, transform: EXT.transform, envelope: EXT.envelope } as const;
const EXPAND_LABELS = { halftone: 'Expand Vector Halftone', transform: 'Expand Transform Effect', envelope: OBJECT_LABEL } as const;

export const expandEffect = objectCommand('vector.effects.expand', backs('halftone-expand', 'transform-effect-expand', 'envelope-expand'), (layer, object, params, diagnostics) => {
  if (!hasExtension(object, EXPAND_KEYS[params.effect])) diagnostics.push(error('COMMAND_REJECTED', `Object ${object.id} has no ${params.effect} effect to expand.`, layer.id));
}, (params) => EXPAND_LABELS[params.effect]);

export const symbolSpray = objectCommand('vector.symbols.spray', backs('symbol-spray'));
export const symbolClear = objectCommand('vector.symbols.clear', backs('symbol-clear'));

// ---------------------------------------------------------------------------
// Variable-width (profile) strokes
// ---------------------------------------------------------------------------

function widthStopIds(object: VectorObject): string[] {
  return object.type === 'path' ? (object.geometry.widthStops ?? []).map((stop) => stop.id) : [];
}

export const widthStopAdd = objectCommand('vector.widthProfile.addStop', backs('width-stop-add'), (layer, object, _params, diagnostics) => {
  requirePath(layer, object, 'A width profile', diagnostics);
  if (!object.stroke) diagnostics.push(error('COMMAND_REJECTED', `Object ${object.id} has no stroke; width points shape the stroke.`, layer.id));
});

export const widthStopUpdate = objectCommand('vector.widthProfile.updateStop', backs('width-stop-position', 'width-stop-width'), (layer, object, params, diagnostics) => {
  requirePath(layer, object, 'A width profile', diagnostics);
  if (object.type === 'path' && !widthStopIds(object).includes(params.stop)) diagnostics.push(error('COMMAND_REJECTED', `Width point ${JSON.stringify(params.stop).slice(0, 80)} does not exist on object ${object.id}.`, layer.id));
});

export const widthStopRemove = objectCommand('vector.widthProfile.removeStop', backs('width-stop-remove'), (layer, object, params, diagnostics) => {
  requirePath(layer, object, 'A width profile', diagnostics);
  if (object.type === 'path' && !widthStopIds(object).includes(params.stop)) diagnostics.push(error('COMMAND_REJECTED', `Width point ${JSON.stringify(params.stop).slice(0, 80)} does not exist on object ${object.id}.`, layer.id));
});

export const widthReset = objectCommand('vector.widthProfile.reset', backs('width-reset-uniform'), (layer, object, _params, diagnostics) => requirePath(layer, object, 'A width profile', diagnostics));

// ---------------------------------------------------------------------------
// Base stroke style and object opacity
// ---------------------------------------------------------------------------

export const strokeStyle = objectCommand('vector.strokeStyle', backs('stroke-cap', 'stroke-join', 'stroke-dash'), (layer, object, _params, diagnostics) => {
  if (!object.stroke) diagnostics.push(error('COMMAND_REJECTED', `Object ${object.id} has no stroke; add one with vector.paint first.`, layer.id));
});

export const objectOpacity = objectCommand('vector.opacity', backs('object-opacity'));

// ---------------------------------------------------------------------------
// Scene operations on several objects
// ---------------------------------------------------------------------------

function objectsCommand<K extends VectorAppearanceCommandId>(id: K, backsIds: readonly string[], label: Spec<K>['label'], check?: (layer: VectorLayer, objects: VectorObject[], params: VectorAppearanceParams[K], diagnostics: Diagnostic[]) => void, mode: LockMode = 'any'): EditorCommand {
  return vectorCommand<K>({
    id,
    backs: backsIds,
    label,
    validate: (document, params, context) => {
      const diagnostics: Diagnostic[] = [];
      const { layer: ref, objects: ids } = params as unknown as ObjectsTarget;
      const layer = vectorLayerFor(document, ref, context, diagnostics, mode);
      if (!layer) return diagnostics;
      const objects = objectsOf(layer, ids, diagnostics);
      if (objects.length === ids.length) check?.(layer, objects, params, diagnostics);
      return diagnostics;
    },
  });
}

export const transformEach = objectsCommand('vector.transformEach', backs('transform-each-apply'), 'Transform Each', undefined, 'position');

export const recolor = objectsCommand('vector.recolor', backs('recolor-apply'), (params) => `Recolor artwork ${params.degrees > 0 ? '+' : ''}${params.degrees}°`);

export const offsetPath = objectsCommand('vector.offsetPath', backs('offset-duplicate'), (params) => `Offset path ${params.distance}px`);

/** blendVectorObjects clamps steps the same way. */
export function blendCount(steps: number | undefined): number {
  return Math.max(1, Math.min(100, Math.round(steps ?? BLEND_STEPS_DEFAULT)));
}

export const blend = vectorCommand({
  id: 'vector.blend',
  backs: backs('blend-create'),
  label: (params) => `Blend ${blendCount(params.steps)} objects`,
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = vectorLayerFor(document, params.layer, context, diagnostics);
    if (!layer) return diagnostics;
    const from = objectOf(layer, params.from, diagnostics);
    const to = objectOf(layer, params.to, diagnostics);
    if (!from || !to) return diagnostics;
    if (from.id === to.id) diagnostics.push(error('COMMAND_REJECTED', 'A blend needs two different objects.', layer.id));
    else if (from.type !== to.type || from.type === 'path') diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Blends join two rectangles or two ellipses; got ${from.type} and ${to.type}.`, layer.id));
    return diagnostics;
  },
});

export const livePaintMake = objectsCommand('vector.livePaint.make', backs('live-paint-make'), 'Create Live Paint Group', (layer, objects, _params, diagnostics) => {
  if (objects.length < 2 || objects.length > 16) diagnostics.push(error('LIMIT_EXCEEDED', 'Live Paint needs 2 to 16 source objects.', layer.id));
});

function livePaintGroup(object: VectorObject): string | null {
  const value = object.extensions[EXT.livePaintFace] as { groupId?: unknown } | undefined;
  return value && typeof value === 'object' && typeof value.groupId === 'string' ? value.groupId : null;
}

export const livePaintFill = objectsCommand('vector.livePaint.fill', backs('live-paint-fill-face'), 'Fill Live Paint Face', (layer, objects, _params, diagnostics) => {
  const plain = objects.filter((object) => !livePaintGroup(object));
  if (plain.length) diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Objects ${plain.map((object) => object.id).join(', ').slice(0, 200)} are not Live Paint faces; make a Live Paint group first.`, layer.id));
});

export const livePaintExpand = objectCommand('vector.livePaint.expand', backs('live-paint-expand'), (layer, object, _params, diagnostics) => {
  if (!livePaintGroup(object)) diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Object ${object.id} is not a Live Paint face.`, layer.id));
}, 'Expand Live Paint Group');

// ---------------------------------------------------------------------------
// Graphic Styles (document resource) and swatch-linked paint
// ---------------------------------------------------------------------------

export const graphicStyleApply = vectorCommand({
  id: 'graphicStyles.apply',
  backs: backs('graphic-style-apply'),
  label: (params) => `${params.mode === 'merge' ? 'Merge' : 'Apply'} Graphic Style`,
  footprint: (_document, params, aliases) => layerWrite(params.layer, aliases, DOCUMENT_RESOURCES.styles),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    requireStyle(document, params.style, diagnostics);
    const layer = vectorLayerFor(document, params.layer, context, diagnostics);
    if (layer) objectsOf(layer, params.objects, diagnostics);
    return diagnostics;
  },
});

export const graphicStyleSave = vectorCommand({
  id: 'graphicStyles.save',
  group: 'resources',
  backs: backs('graphic-style-save'),
  label: 'Save Graphic Style',
  footprint: (_document, params, aliases) => stylesWrite(params.layer, aliases),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    // Reading an object's look does not touch the layer, so its lock does not matter.
    const layer = layerFor(document, params.layer, context, diagnostics);
    if (layer && layer.kind !== 'vector') diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is a ${layer.kind} layer, not vector.`, layer.id));
    else if (layer) objectOf(layer, params.object, diagnostics);
    if (!params.name.trim()) diagnostics.push(error('COMMAND_REJECTED', 'A Graphic Style needs a name.'));
    if (rawStyleIds(document).length >= MAX_GRAPHIC_STYLES) diagnostics.push(error('LIMIT_EXCEEDED', `A document holds at most ${MAX_GRAPHIC_STYLES} Graphic Styles.`));
    return diagnostics;
  },
});

export const graphicStyleRedefine = vectorCommand({
  id: 'graphicStyles.redefine',
  group: 'resources',
  backs: backs('graphic-style-redefine'),
  label: 'Redefine Graphic Style',
  footprint: (_document, params, aliases) => stylesWrite(params.layer, aliases),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, params.layer, context, diagnostics);
    if (layer && layer.kind !== 'vector') diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is a ${layer.kind} layer, not vector.`, layer.id));
    else if (layer) objectOf(layer, params.object, diagnostics);
    requireStyle(document, params.style, diagnostics);
    return diagnostics;
  },
});

function styleOnly<K extends 'graphicStyles.rename' | 'graphicStyles.duplicate' | 'graphicStyles.delete'>(id: K, backsIds: readonly string[], label: string, check?: (document: EditorDocument, params: VectorAppearanceParams[K], diagnostics: Diagnostic[]) => void): EditorCommand {
  return vectorCommand<K>({
    id,
    group: 'resources',
    backs: backsIds,
    label,
    footprint: (_document, _params, aliases) => stylesWrite(undefined, aliases),
    validate: (document, params) => {
      const diagnostics: Diagnostic[] = [];
      requireStyle(document, params.style, diagnostics);
      check?.(document, params, diagnostics);
      return diagnostics;
    },
  });
}

export const graphicStyleRename = styleOnly('graphicStyles.rename', backs('graphic-style-rename'), 'Rename Graphic Style', (_document, params, diagnostics) => {
  if (!params.name.trim()) diagnostics.push(error('COMMAND_REJECTED', 'A Graphic Style needs a name.'));
});
export const graphicStyleDuplicate = styleOnly('graphicStyles.duplicate', backs('graphic-style-duplicate'), 'Duplicate Graphic Style', (document, _params, diagnostics) => {
  if (rawStyleIds(document).length >= MAX_GRAPHIC_STYLES) diagnostics.push(error('LIMIT_EXCEEDED', `A document holds at most ${MAX_GRAPHIC_STYLES} Graphic Styles.`));
});
export const graphicStyleDelete = styleOnly('graphicStyles.delete', backs('graphic-style-delete'), 'Delete Graphic Style');

export const VECTOR_APPEARANCE_COMMANDS: readonly EditorCommand[] = [
  appearanceAdd, appearanceUpdate, appearanceRemove,
  typeOnPath, vectorBrush, halftone, transformEffect, envelope, gradientMesh, expandEffect,
  symbolSpray, symbolClear,
  widthStopAdd, widthStopUpdate, widthStopRemove, widthReset,
  strokeStyle, objectOpacity,
  transformEach, recolor, offsetPath, blend,
  livePaintMake, livePaintFill, livePaintExpand,
  graphicStyleApply, graphicStyleSave, graphicStyleRedefine, graphicStyleRename, graphicStyleDuplicate, graphicStyleDelete,
];
